// src/sagas/videoSubmissionSaga.js
import { takeEvery, call, put } from "redux-saga/effects";
import {
  REQUEST_VIDEO_SUBMISSION_PRESIGNED_URL,
  UPLOAD_VIDEO_FILE,
  requestVideoSubmissionPresignedUrlSuccess,
  requestVideoSubmissionPresignedUrlFailure,
  uploadVideoFileSuccess,
  uploadVideoFileFailure,
} from "../actions/videoSubmissionActions";
import { fetchPresignedUrlsFromApi } from "../apis/libraryCardApi";
import { getUserInfoFromToken } from "../utils/authUtils";
import axios from "axios";

// Saga to handle requesting presigned URL for video submission
function* requestPresignedUrlSaga(action) {
  const { tenantId, fileName, fileType, fileSize, videoId, pipelineTrack } = action.payload;
  
  try {

    console.log("📡 VideoSubmissionSaga - Requesting presigned URL:", {
      tenantId,
      fileName,
      fileType,
      fileSize: `${(fileSize / 1024 / 1024).toFixed(2)} MB`,
      bucket: "fipsr-video-submissions",
      expectedS3Location: `s3://fipsr-video-submissions/${fileName}`,
      fileNameIncludesTenant: fileName.startsWith(`${tenantId}/`),
      videoId: videoId || "Not provided",
    });

    // Request presigned URL using library card API
    // Bucket: fipsr-video-submissions
    // Path: {tenantId}/{fileName} - folder path must be included in fileName
    // Note: Backend generates PUT presigned URLs when operationType is "GET_PRESIGNED_URLS"
    const presignedUrlPayload = {
      operationType: "GET_PRESIGNED_URLS", // Backend handles PUT URLs for this operationType
      entityType: "VIDEO", // Required entityType for video uploads
      tenantId: tenantId,
      bucket: "fipsr-video-submissions", // Correct bucket name
      fileNames: [fileName], // Full path: {tenantId}/{fileName} - folder path included
      tags: ["video", "mp4"], // Optional but recommended
      videoId: videoId, // Include videoId so backend can add it to presigned URL signature
      pipelineTrack: pipelineTrack, // "standard" | "amplify" — signed into x-amz-meta-pipelinetrack
    };

    const presignedUrls = yield call(
      fetchPresignedUrlsFromApi,
      presignedUrlPayload
    );

    if (presignedUrls && presignedUrls.length > 0) {
      const presignedUrl = presignedUrls[0];
      
      // Extract S3 key from presigned URL for logging
      let s3KeyFromUrl = "Unknown";
      let bucketFromUrl = "Unknown";
      try {
        const urlObj = new URL(presignedUrl);
        bucketFromUrl = urlObj.hostname.split('.')[0]; // Extract bucket from hostname
        s3KeyFromUrl = decodeURIComponent(urlObj.pathname.substring(1)); // Remove leading slash and decode
      } catch (e) {
        console.warn("Could not parse S3 key from presigned URL");
      }
      
      console.log("✅ VideoSubmissionSaga - Presigned URL received:", {
        urlLength: presignedUrl.length,
        fileName,
        tenantId,
        s3KeyFromUrl: s3KeyFromUrl,
        bucketFromUrl: bucketFromUrl,
        expectedKey: fileName,
        keyMatches: s3KeyFromUrl === fileName || s3KeyFromUrl === fileName.replace(/^\/+/, ''),
        expectedBucket: "fipsr-video-submissions",
        bucketMatches: bucketFromUrl === "fipsr-video-submissions",
        fullS3Path: `s3://${bucketFromUrl}/${s3KeyFromUrl}`,
        // Don't log full URL for security, just show it exists
        urlPreview: presignedUrl.substring(0, 80) + "...",
      });
      yield put(requestVideoSubmissionPresignedUrlSuccess(presignedUrl));
      // Component will handle the actual upload after receiving presigned URL
    } else {
      console.error("❌ VideoSubmissionSaga - No presigned URL returned from API");
      throw new Error("No presigned URL returned from API");
    }
  } catch (error) {
    console.error("❌ VideoSubmissionSaga - Error requesting presigned URL:", {
      error: error.message,
      stack: error.stack,
      tenantId,
      fileName,
      response: error.response?.data,
      status: error.response?.status,
    });
    yield put(
      requestVideoSubmissionPresignedUrlFailure(
        error.message || "Failed to get presigned URL"
      )
    );
  }
}

// Saga to handle uploading video file to S3
function* uploadVideoFileSaga(action) {
  const { file, presignedUrl, fileName, tenantId, pipelineTrack } = action.payload;
  
  // Get user info for metadata (before it's used)
  const userInfo = getUserInfoFromToken();
  
  try {
    console.log("📤 VideoSubmissionSaga - Starting upload:", {
      fileName,
      fileSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      tenantId,
      userId: userInfo?.userId || "Not available",
    });

    // Prepare metadata headers (matching working implementation pattern from fipsr-with-agents)
    const headers = {
      "Content-Type": file.type || "video/mp4",
    };

    // Add S3 metadata headers (matching exact pattern from working implementation)
    // NOTE: These headers MUST match exactly what the backend includes in the presigned URL signature
    // The backend includes these in the presigned URL query params, so we must send them as headers
    if (tenantId) {
      headers["x-amz-meta-tenantid"] = tenantId;
    }
    // Tags must be JSON stringified array (matching working pattern)
    headers["x-amz-meta-tags"] = JSON.stringify(["video", "mp4"]);
    headers["x-amz-meta-entitytype"] = "VIDEO";
    headers["x-amz-meta-operationtype"] = "GET_PRESIGNED_URLS";
    
    // Add userId and videoId to metadata
    // NOTE: Backend needs to include these in the presigned URL signature for this to work
    // If you get 403 errors, the backend needs to be updated to include these in the signature
    if (userInfo?.userId) {
      headers["x-amz-meta-userid"] = userInfo.userId;
    }
    const { videoId } = action.payload;
    if (videoId) {
      headers["x-amz-meta-videoid"] = videoId;
    }
    if (pipelineTrack) {
      headers["x-amz-meta-pipelinetrack"] = pipelineTrack;
    }

    console.log("📋 VideoSubmissionSaga - Upload headers:", {
      ...headers,
      presignedUrl: "[REDACTED - contains sensitive signature]",
    });

    // Upload file to S3 using presigned URL with axios (matching working implementation)
    const response = yield call(
      axios.put,
      presignedUrl,
      file,
      {
        headers,
        timeout: 10 * 60 * 1000, // 10 minutes for large files
      }
    );

    if (response.status !== 200 && response.status !== 204) {
      throw new Error(`Upload failed with status ${response.status}`);
    }

    console.log("✅ VideoSubmissionSaga - Upload completed successfully:", {
      status: response.status,
      fileName,
      headers: response.headers,
    });
    yield put(uploadVideoFileSuccess());
  } catch (error) {
    console.error("❌ VideoSubmissionSaga - Error uploading file:", {
      error: error.message,
      stack: error.stack,
      fileName,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      code: error.code,
    });
    yield put(
      uploadVideoFileFailure(error.message || "Failed to upload video file")
    );
  }
}

// Watcher sagas
export function* watchRequestPresignedUrl() {
  yield takeEvery(
    REQUEST_VIDEO_SUBMISSION_PRESIGNED_URL,
    requestPresignedUrlSaga
  );
}

export function* watchUploadVideoFile() {
  yield takeEvery(UPLOAD_VIDEO_FILE, uploadVideoFileSaga);
}

// Root saga
export default function* videoSubmissionSaga() {
  yield watchRequestPresignedUrl();
  yield watchUploadVideoFile();
}
